import React, { useState, useEffect, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { fromJS } from 'immutable';
import CallBusyExtensionDialog from 'flow/components/CallBusyExtensionDialog';
import CallMethodDialog from 'flow/components/CallMethodDialog';
import { extractName } from 'flow/utils';
import {
    CallMethodType,
    convertE164NumberToTelavoxRoutingNumber,
    isRegisteredSIP,
} from 'flow/utils/call-utils';
import { addUserPhoneLogEntry } from 'flow/utils/browser-utils';
import { createPresenceNotification } from 'flow/actions/extension-actions';
import { call } from 'flow/actions/phone-actions';
import {
    hideCallDialog,
    setCallDialogType,
} from 'flow/actions/call-dialog-actions';
import { sendPickupCall } from 'flow/actions/top-actions';
import { CallDialogType } from 'flow/types/CallDialogType';
import {
    getCallDialogExtensionToCall,
    getCallDialogNumber,
    getCallMethod,
    getExtraCallOptions,
    isPhoneEnabled,
    selectCallDialogType,
} from 'flow/selectors/selectors';
import { getRingingCall } from 'flow/selectors/call-selectors';
import {
    canSetPresenceNotification,
    selectOwnExtensionKey,
} from 'flow/selectors/ExtensionSelectors';
import useExtension from 'flow/hooks/useExtension';

/**
 * Draws whichever question the call dialog is asking. Which one that is, or whether to ask at
 * all, is settled by showCallDialog in call-dialog-actions when the call is requested — this component
 * only renders the answer to that and reports what the user chooses.
 */
const CallDialogs = () => {
    const dispatch = useAppDispatch();

    const [plusService, setPlusService] = useState<string>('');

    const { ownExtension } = useExtension();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    const number = useAppSelector(getCallDialogNumber);
    const dialogType = useAppSelector(selectCallDialogType);
    const extensionToCall = useAppSelector(getCallDialogExtensionToCall);
    const callMethod = useAppSelector(getCallMethod);
    const phoneEnabled = useAppSelector(isPhoneEnabled);
    const extraCallOptions = useAppSelector(getExtraCallOptions);
    const allowPresenceNotification = useAppSelector(
        (state) =>
            extensionToCall &&
            canSetPresenceNotification(state, extensionToCall.get('key')),
    );
    const ringingCall = useAppSelector(getRingingCall);

    const callAction = useCallback(
        (callMethod: typeof CallMethodType) => {
            const numberToCall: string =
                plusService.length > 0
                    ? convertE164NumberToTelavoxRoutingNumber(number)
                    : number;

            dispatch(
                call(plusService + numberToCall, callMethod, extraCallOptions),
            );
        },
        [dispatch, extraCallOptions, number, plusService],
    );

    const handleClose = useCallback(() => {
        setPlusService('');
        dispatch(hideCallDialog());
    }, [dispatch]);

    const handleBusyExtensionDialogOnCall = useCallback(() => {
        if (callMethod !== CallMethodType.ALWAYS_ASK || !phoneEnabled) {
            callAction(callMethod);
            handleClose();
            return;
        }

        dispatch(setCallDialogType(CallDialogType.CALL_METHOD));
    }, [callAction, handleClose, callMethod, phoneEnabled, dispatch]);

    const handleComonitor = () => {
        setPlusService('*28*');
    };

    useEffect(() => {
        // We need to wait for the plusService variable
        // to be set before making the call
        if (plusService) {
            handleBusyExtensionDialogOnCall();
        }
    }, [plusService, handleBusyExtensionDialogOnCall]);

    if (!number) {
        return null;
    }

    const hasRegisteredTerminal = isRegisteredSIP(ownExtension);

    const handleOnNotify = () => {
        const targetExtensionDTO = fromJS({
            key: extensionToCall.get('key'),
            contact: extensionToCall.get('contact'),
        });

        handleClose();
        dispatch(
            createPresenceNotification(ownExtensionKey, targetExtensionDTO),
        );
    };

    const handleCallMethodDialogOnCall = (
        callMethod: typeof CallMethodType,
    ) => {
        callAction(callMethod);
        handleClose();
    };

    const handlePickupCall = () => {
        const colleague = extractName(extensionToCall.get('contact'));
        dispatch(sendPickupCall());
        addUserPhoneLogEntry(
            `User picked colleague's call with number: ${number} from colleague: ${colleague}`,
        );
        handleClose();
    };

    if (dialogType === CallDialogType.BUSY_EXTENSION) {
        return (
            <CallBusyExtensionDialog
                onCancel={handleClose}
                onNotify={handleOnNotify}
                onCall={handleBusyExtensionDialogOnCall}
                onPickupCall={hasRegisteredTerminal ? handlePickupCall : null}
                onComonitor={handleComonitor}
                extensionToCall={extensionToCall}
                ringingCall={ringingCall}
                callMethod={callMethod}
                allowPresenceNotification={allowPresenceNotification}
            />
        );
    }

    return dialogType === CallDialogType.CALL_METHOD ? (
        <CallMethodDialog
            onCancel={handleClose}
            onCall={handleCallMethodDialogOnCall}
            forComonitor={plusService === '*28*'}
        />
    ) : null;
};

export default React.memo(CallDialogs);
